import { useQuery } from '@tanstack/react-query';
import { deployWorkflowTemplate } from '@lixionary-jira/domain';
import { Globe } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, useAction, useProject } from '../../api/hooks';
import { useIsAdmin } from '../../api/session';
import type { Environment, ProjectSettings } from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { Alert, EmptyState, ErrorAlert, PageUnavailable, QueryView } from '../../ui/feedback';
import { Checkbox, Field, Input, ListEditor, Textarea } from '../../ui/forms';
import { ConfirmDialog, Modal } from '../../ui/Modal';
import { Card, PageHeader, usePageTitle } from '../../ui/misc';
import { SettingsNav } from './SettingsNav';
import { WorkflowCheck } from './WorkflowCheck';

/**
 * PRJ-08: named environments, verification and rollback expectations, completion target, and the GitHub
 * workflow Jira dispatches to deploy each one.
 */
export function EnvironmentsPage() {
  const { key } = useParams();
  const admin = useIsAdmin();
  const project = useProject(key);
  usePageTitle(project.data ? `${project.data.name} · Environments` : null);
  useCrumbs([
    { label: project.data?.name ?? key ?? '', to: `/projects/${key}` },
    { label: 'Settings', to: `/projects/${key}/settings` },
    { label: 'Environments' },
  ]);
  const q = useQuery({
    queryKey: ['settings', key],
    queryFn: () => api.get<ProjectSettings>(`/projects/${key}/settings`),
    enabled: admin,
  });
  const [editing, setEditing] = useState<Environment | 'new' | null>(null);
  const [target, setTarget] = useState<Environment | null>(null);
  const setCompletion = useAction(
    (e: Environment) => api.post(`/projects/${key}/environments/${e.id}/completion-target`),
    {
      success: 'Completion target changed',
      invalidate: [
        ['settings', key],
        ['project', key],
      ],
      onSuccess: () => setTarget(null),
    },
  );
  if (!admin) return <PageUnavailable />;
  const archived = !!project.data?.archived;
  return (
    <div className="page">
      <PageHeader
        title="Environments"
        subtitle="Jira deploys a release by running the environment's GitHub workflow once its pull requests are merged. Only successful delivery to the completion target closes tickets."
        actions={
          <Button onClick={() => setEditing('new')} disabled={archived}>
            Define environment
          </Button>
        }
      />
      <SettingsNav projectKey={key!} />
      <QueryView query={q}>
        {(s) => (
          <>
            {!s.environments.some((e) => e.isProduction) && (
              <Alert tone="warning">
                Define at least one production environment before a production release can be approved.
              </Alert>
            )}
            {s.environments.length === 0 ? (
              <EmptyState icon={Globe} title="No environments yet" />
            ) : (
              <div className="grid-2">
                {s.environments.map((e) => (
                  <Card
                    key={e.id}
                    title={e.name}
                    subtitle={e.description}
                    action={
                      <div className="row">
                        {e.isProduction && <Badge tone="info">Production</Badge>}
                        {e.isCompletionTarget && <Badge tone="success">Completion target</Badge>}
                        {e.requiresApproval && <Badge tone="warning">Requires approval</Badge>}
                      </div>
                    }
                  >
                    <div className="stack sm">
                      <span className="small strong">Deploy workflow</span>
                      {e.deployWorkflow ? (
                        <span className="mono small">{e.deployWorkflow}</span>
                      ) : (
                        <span className="small muted">Not set: releases to {e.name} cannot proceed.</span>
                      )}
                      <span className="small strong">Required CI checks</span>
                      <span className="small">
                        {e.requiredChecks.length ? (
                          e.requiredChecks.join(', ')
                        ) : (
                          <span className="muted">None: any reported checks must still pass</span>
                        )}
                      </span>
                      <span className="small strong">Verification expectations</span>
                      <span className="small" style={{ whiteSpace: 'pre-wrap' }}>
                        {e.verificationExpectations || <span className="muted">Not specified</span>}
                      </span>
                      <span className="small strong">Rollback instructions</span>
                      <span className="small" style={{ whiteSpace: 'pre-wrap' }}>
                        {e.rollbackInstructions || <span className="muted">Not specified</span>}
                      </span>
                    </div>
                    <WorkflowCheck projectKey={key!} env={e} />
                    <div className="row">
                      <Button size="sm" variant="secondary" onClick={() => setEditing(e)} disabled={archived}>
                        Edit
                      </Button>
                      {e.isProduction && !e.isCompletionTarget && (
                        <Button size="sm" variant="ghost" onClick={() => setTarget(e)} disabled={archived}>
                          Make completion target
                        </Button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
            {editing && (
              <EnvironmentDialog
                projectKey={key!}
                env={editing === 'new' ? null : editing}
                onClose={() => setEditing(null)}
              />
            )}
            {target && (
              <ConfirmDialog
                title={`Make ${target.name} the completion target?`}
                confirmLabel="Change completion target"
                onClose={() => setTarget(null)}
                onConfirm={() => setCompletion.mutate(target)}
                pending={setCompletion.isPending}
                error={setCompletion.error}
              >
                <p>Outstanding release approvals in this project become stale and must be reviewed again.</p>
              </ConfirmDialog>
            )}
          </>
        )}
      </QueryView>
    </div>
  );
}

function EnvironmentDialog({
  projectKey,
  env,
  onClose,
}: {
  projectKey: string;
  env: Environment | null;
  onClose: () => void;
}) {
  const [v, setV] = useState({
    name: env?.name ?? '',
    description: env?.description ?? '',
    isProduction: env?.isProduction ?? false,
    verificationExpectations: env?.verificationExpectations ?? '',
    rollbackInstructions: env?.rollbackInstructions ?? '',
    deployWorkflow: env?.deployWorkflow ?? '.github/workflows/deploy.yml',
    requiresApproval: env?.requiresApproval ?? false,
    requiredChecks: env?.requiredChecks ?? [],
  });
  const payload = () => ({ ...v, requiredChecks: v.requiredChecks.map((c) => c.trim()).filter(Boolean) });
  const save = useAction(
    () =>
      env
        ? api.patch(`/projects/${projectKey}/environments/${env.id}`, {
            ...payload(),
            expectedVersion: env.version,
          })
        : api.post(`/projects/${projectKey}/environments`, payload()),
    {
      success: env ? 'Environment updated' : 'Environment defined',
      invalidate: [
        ['settings', projectKey],
        ['project', projectKey],
      ],
      onSuccess: onClose,
    },
  );
  return (
    <Modal
      title={env ? `Edit ${env.name}` : 'Define environment'}
      onClose={onClose}
      wide
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate(undefined)} loading={save.isPending}>
            {env ? 'Save changes' : 'Define environment'}
          </Button>
        </>
      }
    >
      <Field label="Name">
        {(p) => (
          <Input
            {...p}
            value={v.name}
            onChange={(e) => setV({ ...v, name: e.target.value })}
            placeholder="production"
            data-autofocus
          />
        )}
      </Field>
      <Field label="Description" optional>
        {(p) => (
          <Input {...p} value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} />
        )}
      </Field>
      <Checkbox
        label="Production environment"
        description="The first production environment becomes the completion target."
        checked={v.isProduction}
        onChange={(isProduction) => setV({ ...v, isProduction })}
      />
      <Field
        label="Deploy workflow"
        optional
        hint="The GitHub Actions workflow file Jira runs on the default branch to deploy here. Without one, releases to this environment cannot proceed."
      >
        {(p) => (
          <Input
            {...p}
            className="mono"
            value={v.deployWorkflow}
            onChange={(e) => setV({ ...v, deployWorkflow: e.target.value })}
            placeholder=".github/workflows/deploy.yml"
          />
        )}
      </Field>
      <details>
        <summary className="small">Workflow template</summary>
        <p className="small muted">
          Jira runs the workflow with the inputs <span className="mono">revision</span>,{' '}
          <span className="mono">release</span> and <span className="mono">attempt</span>. The run name must
          stay <span className="mono">Deploy REL-n ATT-n &lt;revision&gt;</span> so its result reaches the
          attempt. The <Link to={`/projects/${projectKey}/settings/github-workflows`}>GitHub workflows</Link>{' '}
          tab has the full kit for this project, with CI, setup steps and a prompt for an AI agent.
        </p>
        <pre className="mono small" style={{ overflowX: 'auto' }}>
          {deployWorkflowTemplate(v.name || 'production')}
        </pre>
      </details>
      <div className="field">
        <span className="label">Required CI checks</span>
        <span className="small muted">
          Check run or status names (for GitHub Actions, the job name) that must have passed on the revision
          before a release proceeds. Any check that reports must pass either way; listing one also waits for
          it to appear.
        </span>
        <ListEditor
          label="Check"
          items={v.requiredChecks}
          onChange={(requiredChecks) => setV({ ...v, requiredChecks })}
          addLabel="Add check"
          placeholder="e.g. build"
        />
      </div>
      <Checkbox
        label="Requires release approval"
        description="An admin approves the exact revision before a release proceeds. Merged pull requests are always required."
        checked={v.requiresApproval}
        onChange={(requiresApproval) => setV({ ...v, requiresApproval })}
      />
      <Field label="Verification expectations">
        {(p) => (
          <Textarea
            {...p}
            rows={3}
            value={v.verificationExpectations}
            onChange={(e) => setV({ ...v, verificationExpectations: e.target.value })}
            placeholder="Smoke tests, health endpoint, key user flows"
          />
        )}
      </Field>
      <Field label="Rollback instructions">
        {(p) => (
          <Textarea
            {...p}
            rows={3}
            value={v.rollbackInstructions}
            onChange={(e) => setV({ ...v, rollbackInstructions: e.target.value })}
          />
        )}
      </Field>
      {env && (
        <Alert tone="info">
          Changing this environment invalidates outstanding release approvals that target it.
        </Alert>
      )}
      <ErrorAlert error={save.error} />
    </Modal>
  );
}
